// Story 3.7 + 3.8 — TanStack Query mutation hook for
// PATCH /api/v1/account/preferences.
//
// Mirrors Story 1.16 useTopUpIntent.ts + Story 3.6 useReveal.ts patterns:
//   * Generates the fetch payload from the variables object.
//   * Calls attachRequestIdToScope() for Sentry correlation per Story 1.8.
//   * On non-OK: parses { error: { code, message, field? } } shape +
//     throws so consumers can `.catch` or read useMutation `.error`.
//   * On success: invalidates ["account-me"] so useAccountMe() re-fetches
//     the new preference values (drives useEffectiveMotionMode +
//     useEffectiveCeremonyMode to update).
//
// Story 3.8 added `skip_ceremony_after_graduation?: boolean` — pure
// additive extension, same endpoint, same audit variant (the
// UserPreferencesUpdated event Story 3.7 pre-cemented as multi-field).
//
// Story 4.5 EXTENSION (additive): `public_bio?: string | null` +
// `profile_visibility?: "public" | "private"`. The backend's
// changed_fields audit Vec covers them automatically. v1 contract for
// public_bio: empty string `""` clears the field (UPDATE to NULL) —
// callers that want "preserve current" should OMIT the key (`undefined`).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type AnimationPreferenceValue = "auto" | "reduced" | "full";
export type ProfileVisibilityValue = "public" | "private";

export interface UpdatePreferencesVariables {
  animation_preference?: AnimationPreferenceValue;
  skip_ceremony_after_graduation?: boolean;
  /** Story 4.5 — pass `""` to clear; omit to preserve current value. */
  public_bio?: string;
  profile_visibility?: ProfileVisibilityValue;
}

export interface UpdatePreferencesResponseData {
  animation_preference: AnimationPreferenceValue;
  skip_ceremony_after_graduation: boolean;
  public_bio: string | null;
  profile_visibility: ProfileVisibilityValue;
}

export interface UpdatePreferencesError {
  code: string;
  message: string;
  field?: string;
}

async function patchPreferences(
  variables: UpdatePreferencesVariables,
): Promise<UpdatePreferencesResponseData> {
  const response = await fetch("/api/v1/account/preferences", {
    method: "PATCH",
    credentials: "include",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: UpdatePreferencesError = {
      code: "PREFERENCES_PATCH_FAILED",
      message: `Preferences update failed (HTTP ${response.status})`,
    };
    try {
      const body = await response.json();
      if (body?.error) detail = body.error;
    } catch {
      // Body not JSON — keep the default error shape.
    }
    throw detail;
  }
  const body = (await response.json()) as {
    data: UpdatePreferencesResponseData;
  };
  return body.data;
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  return useMutation<
    UpdatePreferencesResponseData,
    UpdatePreferencesError,
    UpdatePreferencesVariables
  >({
    mutationFn: patchPreferences,
    onSuccess: () => {
      // Drives useAccountMe to re-fetch — useEffectiveMotionMode picks
      // up the new value on the next render across all consumers.
      queryClient.invalidateQueries({ queryKey: ["account-me"] });
    },
  });
}
